import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

export type AdminToastKind = "error" | "success" | "info";

interface AdminToast {
  id: number;
  kind: AdminToastKind;
  message: string;
}

interface AdminToastContextType {
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showInfo: (message: string) => void;
}

const AdminToastContext = createContext<AdminToastContextType | undefined>(undefined);

// Errors read longer than success/info since they usually need to be acted on.
const DURATION_MS: Record<AdminToastKind, number> = {
  error: 7000,
  success: 4000,
  info: 4000,
};

/**
 * Admin pages set error/success state that's rendered inline, at the top of a
 * (often long, scrolled-down) page — so failures from an action taken further
 * down look like nothing happened. Toasts float in a fixed viewport instead,
 * so a message is visible regardless of scroll position.
 */
export const useAdminToast = (): AdminToastContextType => {
  const context = useContext(AdminToastContext);
  if (!context) {
    throw new Error("useAdminToast must be used within an AdminToastProvider");
  }
  return context;
};

interface AdminToastProviderProps {
  children: ReactNode;
}

export const AdminToastProvider: React.FC<AdminToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<AdminToast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: AdminToastKind, message: string) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, kind, message }]);
      window.setTimeout(() => dismiss(id), DURATION_MS[kind]);
    },
    [dismiss],
  );

  const value = useMemo<AdminToastContextType>(
    () => ({
      showError: (message: string) => push("error", message),
      showSuccess: (message: string) => push("success", message),
      showInfo: (message: string) => push("info", message),
    }),
    [push],
  );

  const styles: Record<AdminToastKind, string> = {
    error: "bg-red-900/90 border-red-700 text-red-200",
    success: "bg-green-900/90 border-green-700 text-green-200",
    info: "bg-indigo-900/90 border-indigo-700 text-indigo-200",
  };

  return (
    <AdminToastContext.Provider value={value}>
      {children}
      <div
        className="fixed top-4 right-4 z-[9999] flex w-full max-w-sm flex-col gap-2"
        role="status"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-lg backdrop-blur ${styles[t.kind]}`}
          >
            <span className="flex-1">{t.message}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="shrink-0 text-current opacity-60 hover:opacity-100"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </AdminToastContext.Provider>
  );
};
